import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Amplify } from 'aws-amplify';

import { CookieStorage, Hub } from 'aws-amplify/utils';
import {
  cognitoUserPoolsTokenProvider,
  SignUpOutput,
  SignInOutput,
} from 'aws-amplify/auth/cognito';
import {
  AuthUser,
  confirmResetPassword as amplifyConfirmResetPassword,
  ConfirmResetPasswordInput,
  confirmSignIn as amplifyConfirmSignIn,
  ConfirmSignInInput,
  confirmSignUp as amplifyConfirmSignUp,
  ConfirmSignUpInput,
  ConfirmSignUpOutput,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode as amplifyResendSignUpCode,
  ResendSignUpCodeInput,
  ResendSignUpCodeOutput,
  resetPassword as amplifyResetPassword,
  ResetPasswordInput,
  ResetPasswordOutput,
  signIn as amplifyAuthSignIn,
  SignInInput,
  signInWithRedirect as amplifyAuthSignInWithRedirect,
  signOut as amplifyAuthSignOut,
  signUp as amplifySignUp,
  SignUpInput,
  updatePassword as amplifyUpdatePassword,
  autoSignIn as amplifyAutoSignIn,
  UpdatePasswordInput,
} from 'aws-amplify/auth';
import { useErrorHandler } from '@sandtech-hub-monorepo/ui-kit';
import Cookies from 'js-cookie';
import { AuthProvider } from '@aws-amplify/auth/src/types/inputs';

export type SocialProviderType = AuthProvider | { custom: string };

const PENDING_SOCIAL_PROVIDER_KEY = 'ehub:pendingSocialProvider';

const normalizeSocialProvider = (provider: SocialProviderType): string => {
  if (typeof provider === 'string') return provider.toLowerCase();
  return provider.custom.toLowerCase();
};

export interface CognitoContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  signIn: (input: SignInInput) => Promise<void | SignInOutput>;
  socialSignIn: (provider: SocialProviderType) => void;
  signOut: () => void;
  signUp: (input: SignUpInput) => Promise<SignUpOutput | void>;
  resendSignUpCode: (
    input: ResendSignUpCodeInput
  ) => Promise<ResendSignUpCodeOutput | void> | undefined;
  confirmSignUp: (
    input: ConfirmSignUpInput
  ) => Promise<ConfirmSignUpOutput | void>;
  changePassword: (
    input: UpdatePasswordInput,
    onSuccess: () => void
  ) => Promise<void>;
  resetPassword: (
    input: ResetPasswordInput
  ) => Promise<ResetPasswordOutput | void>;
  confirmResetPassword: (input: ConfirmResetPasswordInput) => Promise<void>;
  confirmSignIn: (input: ConfirmSignInInput) => Promise<void>;
  autoSignIn: () => Promise<SignInOutput | void>;
}

const CognitoContext = createContext<CognitoContextType>(
  {} as CognitoContextType
);

export interface CognitoProviderProps {
  children: React.ReactNode;
  /**
   * Fired after a successful social (OAuth redirect) sign in. The provider
   * name is lowercased (e.g. 'google', 'facebook', 'linkedin'). This hook
   * covers both first-time social signups and returning social logins —
   * amplify does not distinguish between them at the Hub layer.
   */
  onSocialSignIn?: (provider: string) => void;
}

export const CognitoProvider = ({
  children,
  onSocialSignIn,
}: CognitoProviderProps) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const { handleAuthError } = useErrorHandler();

  const cookieDomain =
    process.env['NX_COOKIE_DOMAIN'] ?? window.location.hostname;

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', (message) => {
      switch (message.payload.event) {
        case 'signedIn':
          setCurrentUser(message.payload.data);
          Cookies.set('eHubLoggedIn', 'true', { domain: cookieDomain });
          break;

        case 'signedOut':
          setCurrentUser(null);
          Cookies.remove('eHubLoggedIn', { domain: cookieDomain });
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          window?.pendo?.clearSession();
          break;
        case 'signInWithRedirect': {
          const pendingProvider = sessionStorage.getItem(
            PENDING_SOCIAL_PROVIDER_KEY
          );
          if (pendingProvider) {
            sessionStorage.removeItem(PENDING_SOCIAL_PROVIDER_KEY);
          }
          onSocialSignIn?.(pendingProvider ?? 'unknown');
          break;
        }
        case 'customOAuthState':
          console.log(message.payload.message);
          break;
        case 'signInWithRedirect_failure':
          sessionStorage.removeItem(PENDING_SOCIAL_PROVIDER_KEY);
          console.log(message);
          break;
        default:
          console.log(message);
          break;
      }
    });

    return unsubscribe;
  });

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        Cookies.set('eHubLoggedIn', 'true', { domain: cookieDomain });
        setCurrentUser(user);
      })
      .catch((e) => {
        Cookies.remove('eHubLoggedIn', { domain: cookieDomain });
        setCurrentUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(
    (input: SignInInput) => {
      setLoading(true);

      return amplifyAuthSignIn(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const socialSignIn = useCallback(
    (provider: SocialProviderType) => {
      // Persist the provider so we can recover it after the OAuth redirect
      // returns and the Hub emits `signInWithRedirect` (page has reloaded).
      sessionStorage.setItem(
        PENDING_SOCIAL_PROVIDER_KEY,
        normalizeSocialProvider(provider)
      );
      amplifyAuthSignInWithRedirect({
        provider: provider,
      }).catch((error) => {
        sessionStorage.removeItem(PENDING_SOCIAL_PROVIDER_KEY);
        handleAuthError(error);
      });
    },
    [handleAuthError]
  );

  const signOut = useCallback(() => {
    setLoading(true);
    amplifyAuthSignOut()
      .then(() => {
        setCurrentUser(null);
        Cookies.remove('eHubLoggedIn');
      })
      .catch(handleAuthError)
      .finally(() => setLoading(false));
  }, [handleAuthError]);

  const signUp = useCallback(
    async (input: SignUpInput) => {
      setLoading(true);
      return amplifySignUp(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const resendSignUpCode = useCallback(
    (input: ResendSignUpCodeInput) => {
      setLoading(true);
      return amplifyResendSignUpCode(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));

      return;
    },
    [handleAuthError]
  );

  const confirmSignUp = useCallback(
    async (input: ConfirmSignUpInput) => {
      setLoading(true);
      return amplifyConfirmSignUp(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const autoSignIn = useCallback(async () => {
    setLoading(true);
    return amplifyAutoSignIn()
      .catch(handleAuthError)
      .finally(() => setLoading(false));
  }, [handleAuthError]);

  const changePassword = useCallback(
    async (input: UpdatePasswordInput, onSuccess: () => void) => {
      setLoading(true);
      return amplifyUpdatePassword(input)
        .then(onSuccess)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const resetPassword = useCallback(
    async (input: ResetPasswordInput) => {
      setLoading(true);
      return amplifyResetPassword(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const confirmResetPassword = useCallback(
    async (input: ConfirmResetPasswordInput) => {
      setLoading(true);
      await amplifyConfirmResetPassword(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const confirmSignIn = useCallback(
    async (input: ConfirmSignInInput) => {
      setLoading(true);
      await amplifyConfirmSignIn(input)
        .catch(handleAuthError)
        .finally(() => setLoading(false));
    },
    [handleAuthError]
  );

  const value = useMemo(
    () => ({
      currentUser,
      loading,
      signIn,
      socialSignIn,
      signOut,
      signUp,
      resendSignUpCode,
      confirmSignUp,
      changePassword,
      resetPassword,
      confirmResetPassword,
      confirmSignIn,
      autoSignIn,
    }),
    [
      currentUser,
      loading,
      signIn,
      socialSignIn,
      signOut,
      signUp,
      resendSignUpCode,
      confirmSignUp,
      changePassword,
      resetPassword,
      confirmResetPassword,
      confirmSignIn,
      autoSignIn,
    ]
  );

  return (
    <CognitoContext.Provider value={value}>{children}</CognitoContext.Provider>
  );
};

export const useCognito = () => useContext(CognitoContext);

export const configureAmplify = (
  userPoolId: string,
  userPoolClientId: string,
  userPoolDomain: string,
  redirectSignIn: string[],
  redirectSignOut: string[],
  cookieDomain?: string
) => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: userPoolId,
        userPoolClientId: userPoolClientId,
        loginWith: {
          oauth: {
            scopes: ['email', 'profile', 'openid'],
            domain: userPoolDomain,
            redirectSignIn: redirectSignIn,
            redirectSignOut: redirectSignOut,
            responseType: 'code',
          },
          username: true,
        },
      },
    },
  });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: cookieDomain ?? window.location.hostname,
      path: '/',
      expires: 7,
      secure: true,
    })
  );
};

export const getIdToken = async () => {
  try {
    let session = await fetchAuthSession();
    let idToken = session?.tokens?.idToken;

    // If no token returned (can happen on initial load with expired token),
    // retry with forceRefresh to trigger token refresh
    if (!idToken) {
      session = await fetchAuthSession({ forceRefresh: true });
      idToken = session?.tokens?.idToken;
    }

    return idToken?.toString();
  } catch (e) {
    return undefined;
  }
};
